import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCountiesForRegistration, useMunicipalitiesForRegistration, useRegisterUser } from "../api/registrations";
import "./login.css";

// SystemRole enum values (from CenterReach.Domain.Enums.SystemRole)
// Distributor = 5
const DISTRIBUTOR_ROLE = 5;

export const Register = (): JSX.Element => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);
    const [requestedMunicipalityId, setRequestedMunicipalityId] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const { data: counties = [], isLoading: countiesLoading, error: countiesError } = useCountiesForRegistration();
    const { data: municipalities = [], isLoading: municipalitiesLoading, error: municipalitiesError } =
        useMunicipalitiesForRegistration(selectedCountyId ?? undefined);
    const registerMutation = useRegisterUser();

    const handleCountyChange = useCallback((countyId: string | null) => {
        setSelectedCountyId(countyId);
        setRequestedMunicipalityId("");
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        if (password !== confirmPassword) {
            setError("Lösenorden matchar inte");
            return;
        }

        if (password.length < 8) {
            setError("Lösenordet måste vara minst 8 tecken");
            return;
        }

        if (!requestedMunicipalityId) {
            setError("Välj en krets");
            return;
        }

        try {
            await registerMutation.mutateAsync({
                email: email.trim(),
                userName: email.trim(),
                password,
                requestedMunicipalityPolygonId: requestedMunicipalityId,
                requestedRole: DISTRIBUTOR_ROLE,
            });
            setSuccess(true);
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
            setError(
                axiosErr.response?.data?.message ||
                    axiosErr.message ||
                    "Kunde inte registrera användare. Försök igen."
            );
        }
    };

    if (success) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h1>{t("app.title")}</h1>
                        <p className="login-subtitle">Registrering skickad</p>
                    </div>
                    <div style={{ textAlign: "center", padding: "2rem 0" }}>
                        <p>Din registrering har skickats och väntar på godkännande från en administratör.</p>
                        <p style={{ marginTop: "1rem", fontSize: "0.9rem", color: "rgba(226, 232, 240, 0.7)" }}>
                            Du omdirigeras till inloggningssidan om några sekunder...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const hasDataError = countiesError || municipalitiesError;

    return (
        <div className="login-container">
            <div className="login-card" style={{ maxWidth: "500px" }}>
                <div className="login-header">
                    <h1>{t("app.title")}</h1>
                    <p className="login-subtitle">Registrera ny användare</p>
                </div>

                {hasDataError && (
                    <div className="login-error" role="alert">
                        Kunde inte ladda län/kommuner. Försök igen senare.
                    </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                    {error && (
                        <div className="login-error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="login-field">
                        <label htmlFor="email">E-post *</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="namn@example.com"
                            required
                            autoComplete="email"
                            disabled={registerMutation.isPending}
                        />
                        <p className="login-hint">E-postadressen används också som användarnamn</p>
                    </div>

                    <div className="login-field">
                        <label htmlFor="password">Lösenord *</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Lösenord (minst 8 tecken)"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            disabled={registerMutation.isPending}
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="confirmPassword">Bekräfta lösenord *</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Bekräfta lösenord"
                            required
                            autoComplete="new-password"
                            disabled={registerMutation.isPending}
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="county">Län</label>
                        <select
                            id="county"
                            value={selectedCountyId ?? ""}
                            onChange={(e) => handleCountyChange(e.target.value || null)}
                            disabled={registerMutation.isPending || countiesLoading}
                            style={{
                                padding: "0.75rem 1rem",
                                border: "1px solid rgba(148, 163, 184, 0.2)",
                                borderRadius: "0.5rem",
                                fontSize: "1rem",
                                backgroundColor: "rgba(15, 23, 42, 0.8)",
                                color: "#f8fafc",
                                cursor: countiesLoading || registerMutation.isPending ? "not-allowed" : "pointer",
                            }}
                        >
                            <option value="">Välj län...</option>
                            {counties.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                        {countiesLoading && <p className="login-hint">Laddar län...</p>}
                    </div>

                    <div className="login-field">
                        <label htmlFor="krets">Krets *</label>
                        <select
                            id="krets"
                            value={requestedMunicipalityId}
                            onChange={(e) => setRequestedMunicipalityId(e.target.value)}
                            required
                            disabled={registerMutation.isPending || municipalitiesLoading}
                            style={{
                                padding: "0.75rem 1rem",
                                border: "1px solid rgba(148, 163, 184, 0.2)",
                                borderRadius: "0.5rem",
                                fontSize: "1rem",
                                backgroundColor: "rgba(15, 23, 42, 0.8)",
                                color: "#f8fafc",
                                cursor: municipalitiesLoading || registerMutation.isPending ? "not-allowed" : "pointer",
                            }}
                        >
                            <option value="">
                                {selectedCountyId ? "Välj krets..." : "Välj län först..."}
                            </option>
                            {municipalities.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.name} ({m.code})
                                </option>
                            ))}
                        </select>
                        {municipalitiesLoading && <p className="login-hint">Laddar kommuner...</p>}
                    </div>

                    <button
                        type="submit"
                        className="login-button"
                        disabled={registerMutation.isPending || countiesLoading || municipalitiesLoading}
                    >
                        {registerMutation.isPending ? "Registrerar..." : "Registrera"}
                    </button>
                </form>

                <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    <Link
                        to="/login"
                        style={{
                            color: "#93c5fd",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                        }}
                    >
                        Tillbaka till inloggning
                    </Link>
                </div>
            </div>
        </div>
    );
};
